// src/features/admin/adminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminService from './adminService';

const initialState = {
    users: [],
    settings: null,
    isLoading: false,
    isSuccess: false,
    isError: false,
    message: '',
};

// Fetch all users
export const fetchUsers = createAsyncThunk(
    'admin/fetchUsers',
    async (_, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await adminService.getUsers(token);
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();

            return thunkAPI.rejectWithValue({ message });
        }
    }
);

// Update user role
export const updateUserRole = createAsyncThunk(
    'admin/updateUserRole',
    async (userData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await adminService.updateUserRole(userData.userId, userData.role, token);
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();

            return thunkAPI.rejectWithValue({ message });
        }
    }
);

// Toggle user status (activate/deactivate)
export const toggleUserStatus = createAsyncThunk(
    'admin/toggleUserStatus',
    async (userData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await adminService.toggleUserStatus(userData.userId, userData.isActive, token);
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();

            return thunkAPI.rejectWithValue({ message });
        }
    }
);

// Delete user
export const deleteUser = createAsyncThunk(
    'admin/deleteUser',
    async (userId, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            await adminService.deleteUser(userId, token);
            return userId;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();

            return thunkAPI.rejectWithValue({ message });
        }
    }
);

// Fetch system settings
export const fetchSystemSettings = createAsyncThunk(
    'admin/fetchSystemSettings',
    async (_, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await adminService.getSystemSettings(token);
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();

            return thunkAPI.rejectWithValue({ message });
        }
    }
);

// Update system settings
export const updateSystemSettings = createAsyncThunk(
    'admin/updateSystemSettings',
    async (settingsData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            return await adminService.updateSystemSettings(settingsData, token);
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();

            return thunkAPI.rejectWithValue({ message });
        }
    }
);

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        resetAdminState: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = '';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch users cases
            .addCase(fetchUsers.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.users = action.payload;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Update user role cases
            .addCase(updateUserRole.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateUserRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.users = state.users.map((user) =>
                    user.id === action.payload.id ? action.payload : user
                );
            })
            .addCase(updateUserRole.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Toggle user status cases
            .addCase(toggleUserStatus.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(toggleUserStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.users = state.users.map((user) =>
                    user.id === action.payload.id ? action.payload : user
                );
            })
            .addCase(toggleUserStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Delete user cases
            .addCase(deleteUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.users = state.users.filter((user) => user.id !== action.payload);
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Fetch system settings cases
            .addCase(fetchSystemSettings.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchSystemSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.settings = action.payload;
            })
            .addCase(fetchSystemSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            })

            // Update system settings cases
            .addCase(updateSystemSettings.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateSystemSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.settings = action.payload;
            })
            .addCase(updateSystemSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload.message;
            });
    },
});

export const { resetAdminState } = adminSlice.actions;
export default adminSlice.reducer;
